import { useCallback, useState, type KeyboardEvent } from 'react';

import type { DxfLayer } from '@frontend/api';
import {
  Box,
  type BoxProps,
  Checkbox,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeCrossedIcon,
  EyeOpenIcon,
  Flex,
  Text,
  Tooltip,
} from '@frontend/legacy-components';

export type DxfLayerPanelProps = {
  /** Layers from the DXF scene (`scene.layers`), rendered top-to-bottom. */
  layers: readonly DxfLayer[];
  /** Layer indices to hide; an index absent from the set is visible. */
  hiddenLayers?: ReadonlySet<number>;
  /** Toggle a single layer. */
  onToggleLayer: (layerIndex: number, nextVisible: boolean) => void;
  /** Clicking the layer's name selects its geometry; Shift adds to the selection. */
  onSelectLayer?: (layerIndex: number, isAdditive: boolean) => void;
  /** Double-clicking the name frames the layer. */
  onFrameLayer?: (layerIndex: number) => void;
  /** Hovering a row highlights the layer on the canvas; `null` on leave. */
  onHoverLayer?: (layerIndex: number | null) => void;
  /** Per-layer source-entity counts, keyed by layer index, shown as a badge. */
  counts?: ReadonlyMap<number, number>;
  /** Layers that have stitched geometry, and so can be selected by name. */
  selectableLayers?: ReadonlySet<number>;
  /**
   * Layers that are hidden but still showing something — PMI outlives a layer
   * toggle — so their checkbox reads as neither on nor off.
   */
  indeterminateLayers?: ReadonlySet<number>;
  /** Whether the drawing is painted in its layers' own colours. */
  isLayerColoringOn?: boolean;
  /** Toggles layer colouring. Independent of every other colouring source. */
  onToggleLayerColoring?: () => void;
  /** Panel heading. */
  title?: string;
  /** Whether the panel starts expanded. */
  defaultOpen?: boolean;
} & BoxProps;

type DxfLayerRowProps = {
  layer: DxfLayer;
  visible: boolean;
  count?: number;
  onToggle: (nextVisible: boolean) => void;
  /** Hidden, but still drawing something. */
  isIndeterminate?: boolean;
  onSelect?: (isAdditive: boolean) => void;
  onFrame?: () => void;
  onHover?: (isHovering: boolean) => void;
  /** A layer with no stitched geometry has nothing to select, so it stays inert. */
  isSelectable?: boolean;
};

/**
 * A single layer row: a real, accessible checkbox recoloured with the layer's
 * own colour via `accentColor`, whose label spans the full row width so
 * clicking anywhere in the row toggles visibility. Unchecked shows a
 * darkened-accent outline rather than the Figma spec's tinted fill (node
 * 71:6301) — an intentional deviation to match the shared Checkbox component.
 */
const DxfLayerRow = ({
  layer,
  visible,
  count,
  onToggle,
  isIndeterminate = false,
  onSelect,
  onFrame,
  onHover,
  isSelectable = false,
}: DxfLayerRowProps) => {
  const color = layer.style.rgb;
  const label = layer.name || '0';

  const handleToggle = useCallback(() => {
    onToggle(!visible);
  }, [onToggle, visible]);

  // The name is its own control so that clicking it selects rather than toggling
  // visibility, while the rest of the row stays the checkbox's own label.
  const handleSelect = useCallback(
    (event: React.MouseEvent) => {
      if (!isSelectable) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.detail > 1) {
        onFrame?.();
        return;
      }
      onSelect?.(event.shiftKey);
    },
    [isSelectable, onFrame, onSelect],
  );

  return (
    <Checkbox
      accentColor={color}
      isChecked={visible}
      isIndeterminate={isIndeterminate}
      onChange={handleToggle}
      aria-label={`${visible ? 'Hide' : 'Show'} layer ${label}`}
      display="flex"
      width="100%"
      paddingX="6px"
      paddingY="2px"
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      _hover={{ bg: 'interaction.surfaceHover' }}
      sx={{ '.chakra-checkbox__label': { flex: '1 1 auto', minWidth: 0 } }}
    >
      <Flex align="center" justify="space-between" gap={2} minW={0}>
        <Text
          flex="1 1 auto"
          noOfLines={1}
          fontSize="14px"
          fontWeight={500}
          color="feature.primary"
          title={label}
          cursor={isSelectable ? 'pointer' : undefined}
          onClick={handleSelect}
        >
          {label}
        </Text>
        {count !== undefined && (
          <Flex
            flexShrink={0}
            align="center"
            paddingX="3px"
            borderRadius="2px"
            bg="page.plateMedium"
          >
            <Text
              fontSize="10px"
              lineHeight="16px"
              fontWeight="semibold"
              color="feature.secondary"
            >
              {count}
            </Text>
          </Flex>
        )}
      </Flex>
    </Checkbox>
  );
};

/**
 * Floating, collapsible "File Layers" panel for the new DXF viewer.
 *
 * Pure and controlled: renders the scene's layers with an accent-coloured checkbox
 * per row (the layer's own colour) and reports toggles up via `onToggleLayer`.
 * Kept free of the session so it stays trivially testable — `DxfLayerPanelHost`
 * is the piece that subscribes. Based on the Figma design (nodes 125:33444 /
 * 71:6301), with the checkbox itself swapped for the shared `Checkbox`'s
 * `accentColor` styling.
 */
export const DxfLayerPanel = ({
  layers,
  hiddenLayers,
  onToggleLayer,
  onSelectLayer,
  onFrameLayer,
  onHoverLayer,
  counts,
  selectableLayers,
  indeterminateLayers,
  isLayerColoringOn = true,
  onToggleLayerColoring,
  title = 'Layers',
  defaultOpen = true,
  ...props
}: DxfLayerPanelProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleLayerColoringKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!onToggleLayerColoring) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleLayerColoring();
      }
    },
    [onToggleLayerColoring],
  );

  return (
    <Box
      bg="page.surface"
      border="1px solid"
      borderColor="separator.border"
      borderRadius="lg"
      boxShadow="popover"
      overflow="hidden"
      userSelect="none"
      {...props}
    >
      <Flex
        align="center"
        gap={2}
        width="100%"
        paddingX="12px"
        paddingY="8px"
        {...(isOpen && {
          borderBottom: '1px solid',
          borderColor: 'separator.divider',
        })}
      >
        <Flex
          as="button"
          type="button"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
          align="center"
          gap={2}
          flex="1 1 auto"
          minW={0}
        >
          {isOpen ? (
            <ChevronDownIcon boxSize="18px" color="feature.primary" />
          ) : (
            <ChevronRightIcon boxSize="18px" color="feature.primary" />
          )}
          <Text fontSize="14px" fontWeight={600} color="feature.primary">
            {title}
          </Text>
        </Flex>
        {/* Layer colouring is its own toggle, deliberately separate from the
            chevron: collapsing the panel must not change what the canvas shows. */}
        {onToggleLayerColoring && (
          <Tooltip
            placement="top"
            label={isLayerColoringOn ? 'Layer colours on' : 'Layer colours off'}
          >
            <Box
              as="div"
              role="button"
              tabIndex={0}
              aria-label="Toggle layer colours"
              aria-pressed={isLayerColoringOn}
              onClick={onToggleLayerColoring}
              onKeyDown={handleLayerColoringKeyDown}
              display="flex"
              alignItems="center"
              flexShrink={0}
              cursor="pointer"
              color={
                isLayerColoringOn ? 'feature.primary' : 'feature.placeholder'
              }
            >
              {isLayerColoringOn ? (
                <EyeOpenIcon boxSize="18px" />
              ) : (
                <EyeCrossedIcon boxSize="18px" />
              )}
            </Box>
          </Tooltip>
        )}
      </Flex>

      {isOpen && (
        <Box paddingX="12px" paddingY="8px" maxHeight="320px" overflowY="auto">
          {layers.length === 0 ? (
            <Text paddingX="6px" fontSize="14px" color="feature.secondary">
              No layers
            </Text>
          ) : (
            layers.map((layer, index) => (
              <DxfLayerRow
                key={layer.name}
                layer={layer}
                visible={!hiddenLayers?.has(index)}
                count={counts?.get(index)}
                isSelectable={selectableLayers?.has(index) ?? false}
                isIndeterminate={indeterminateLayers?.has(index) ?? false}
                onToggle={(nextVisible) => onToggleLayer(index, nextVisible)}
                onSelect={(isAdditive) => onSelectLayer?.(index, isAdditive)}
                onFrame={() => onFrameLayer?.(index)}
                onHover={(isHovering) =>
                  onHoverLayer?.(isHovering ? index : null)
                }
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
};
